// Voce del dispositivo: italiana vera, gratuita, con campanella per reparto.
// Cinque trappole gestite qui: sblocco al primo tocco, voci che arrivano in ritardo,
// dispositivi senza voci italiane, punteggiatura per la cucina rumorosa, un solo audio per volta.

const TONI = {
  cucina: 660,
  pizzeria: 880,
  bar: 990,
  pasticceria: 1046,
  "tavola calda": 740,
  "tavola fredda": 587,
  default: 784,
};

// Preferenze per nome: iOS (Alice, Federica), Android/Chrome (Google italiano), Windows (Elsa)
const PREFERITE = ["alice", "federica", "luca", "elsa", "google italiano", "italiano", "italy"];

let ctx = null;
let sbloccata = false;
let voci = [];

/** Un solo contesto audio per tutta l'app: su iPhone uno creato fuori da un tocco nasce muto. */
export const audioCtx = () => {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume?.();
    return ctx;
  } catch {
    return null;
  }
};

/** Al primo tocco (o tasto) l'audio si sblocca da solo: su iPhone serve un gesto vero. */
export const preparaAudio = () => {
  const apri = () => {
    const c = audioCtx();
    if (c) {
      // suono muto: basta a far "svegliare" l'audio su iOS senza farsi sentire
      try {
        const o = c.createOscillator();
        const gain = c.createGain();
        gain.gain.value = 0;
        o.connect(gain);
        gain.connect(c.destination);
        o.start();
        o.stop(c.currentTime + 0.02);
      } catch { /* niente audio su questo dispositivo */ }
      sbloccata = true;
    }
    window.removeEventListener("pointerdown", apri);
    window.removeEventListener("touchend", apri);
    window.removeEventListener("keydown", apri);
  };
  window.addEventListener("pointerdown", apri, { once: false });
  window.addEventListener("touchend", apri, { once: false });
  window.addEventListener("keydown", apri, { once: false });
};

const carica = () => {
  if (!("speechSynthesis" in window)) return;
  voci = window.speechSynthesis.getVoices().filter((v) => (v.lang || "").toLowerCase().startsWith("it"));
};

if ("speechSynthesis" in window) {
  carica();
  window.speechSynthesis.onvoiceschanged = carica; // le voci arrivano in ritardo
}

export const vociItaliane = () => {
  if (!voci.length) carica();
  return voci.map((v) => ({ name: v.name, lang: v.lang }));
};

export const vocePronta = () => sbloccata;

// iOS e Chrome bloccano audio e voce finché l'utente non tocca la pagina
export const sbloccaVoce = () =>
  new Promise((resolve) => {
    try {
      ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
      ctx.resume?.();
      carica();
      if ("speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance("Lettura attiva.");
        u.lang = "it-IT";
        u.rate = 0.95;
        const scelta = pickVoice("default");
        if (scelta) u.voice = scelta;
        u.onend = () => resolve(true);
        u.onerror = () => resolve(true);
        window.speechSynthesis.speak(u);
      } else {
        resolve(true);
      }
      sbloccata = true;
    } catch {
      sbloccata = true;
      resolve(false);
    }
  });

// Schermo sempre acceso: in cucina il tablet non deve andare in stand-by, altrimenti
// la voce non parte. Più semplice e affidabile delle notifiche push.
let lock = null;

export const tieniAcceso = async (attivo) => {
  try {
    if (!("wakeLock" in navigator)) return false;
    if (attivo) {
      if (lock && !lock.released) return true;
      lock = await navigator.wakeLock.request("screen");
      lock.addEventListener?.("release", () => {
        lock = null;
      });
      return true;
    }
    await lock?.release?.();
    lock = null;
    return true;
  } catch {
    return false;
  }
};

export const riprendiAcceso = () => {
  if (document.visibilityState === "visible" && !lock) tieniAcceso(true);
};

export const ding = (reparto = "default") => {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    const freq = TONI[(reparto || "").toLowerCase()] || TONI.default;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.6);
  } catch {
    /* audio non disponibile: si prosegue senza campanella */
  }
};

export const pickVoice = (reparto = "default", salvata = "") => {
  if (!voci.length) carica();
  if (!voci.length) return null;
  if (salvata) {
    const esatta = voci.find((v) => v.name === salvata);
    if (esatta) return esatta;
  }
  for (const nome of PREFERITE) {
    const trovata = voci.find((v) => v.name.toLowerCase().includes(nome));
    if (trovata) return trovata;
  }
  // Voci diverse per reparto, così si distingue chi sta parlando
  const ordine = ["cucina", "pizzeria", "bar", "pasticceria", "tavola calda", "tavola fredda"];
  const i = Math.max(0, ordine.indexOf((reparto || "").toLowerCase()));
  return voci[i % voci.length];
};

export const speak = (testo, reparto = "default", salvata = "") => {
  if (!("speechSynthesis" in window) || !testo) return false;
  const rep = (reparto || "default").toLowerCase();
  const u = new SpeechSynthesisUtterance(testo);
  u.lang = "it-IT";
  const v = pickVoice(rep, salvata);
  if (v) u.voice = v;
  // In cucina e alla tavola calda più grave e lenta, in pizzeria e bar più chiara
  u.pitch = rep === "cucina" || rep === "tavola calda" ? 0.85 : rep === "pizzeria" ? 1.05 : 1;
  u.rate = rep === "cucina" || rep === "tavola calda" ? 0.9 : 0.95;
  u.volume = 1;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
  return true;
};

// La punteggiatura conta più della velocità: virgole e puntini rendono la comanda comprensibile
export const ticketSpeech = (righe, { completo = true, asportoLabel = "Asporto", tavoloLabel = "Tavolo" } = {}) => {
  const lista = Array.isArray(righe) ? righe : [];
  if (!lista.length) return typeof righe === "string" ? righe : "";
  const parti = ["Nuova comanda, ..."];
  lista.forEach((r) => {
    const dove = r.mode === "asporto" ? `${asportoLabel} ${r.tables?.[0] ?? ""}` : `${tavoloLabel} ${(r.tables || []).join(" e ")}`;
    parti.push(`${dove}, ...`);
    const daFare = (r.items || []).filter((i) => !i.ready);
    if (!completo) {
      const n = daFare.reduce((s, i) => s + (i.qty || 1), 0);
      parti.push(n ? `${n === 1 ? "un piatto" : `${n} piatti`}, ...` : "tutto pronto, ...");
      return;
    }
    daFare.forEach((i) => parti.push(`${i.qty} ${i.name}${i.note ? `, ${i.note}` : ""}, ...`));
  });
  return parti.join(" ");
};

export const annuncia = (righe, reparto, opzioni = {}) => {
  ding(reparto);
  const frase = ticketSpeech(righe, opzioni) || (typeof righe === "string" ? righe : "");
  if (frase) setTimeout(() => speak(frase, reparto, opzioni.salvata), 650);
  return frase;
};
